import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Trash2, AlertTriangle, ShieldAlert, Info, CheckCircle2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  getNotificationsApi,
  markReadApi,
  markAllReadApi,
  clearAllNotificationsApi,
} from "../../services/axios/AuthApis/NotificationApis";

const POLL_INTERVAL_MS = 20_000;
const BRAND_COLOR = "#26256b";

const CATEGORY_BY_EVENT_TYPE = {
  new_critical_finding: "Findings",
  finding_reopened: "Findings",
  new_rule_added: "Rules",
  rule_updated: "Rules",
  rule_deleted: "Rules",
  new_asset: "Asset",
  asset_updated: "Asset",
  asset_deleted: "Asset",
  classification_changed: "Classification",
  new_incident: "Incident",
  incident_status_changed: "Incident",
  new_investigation: "Investigation",
  new_response_action: "Response",
  response_action_status_changed: "Response",
  new_high_severity_asset: "Asset",
  new_asset: "Asset",
  compliance_score_drop: "Compliance",
  control_failed: "Compliance",
  risk_score_increase: "Risk",
  risk_level_change: "Risk",
  remediation_needed: "Remediation",
  remediation_completed: "Remediation",
  scan_completed: "Scan",
  scan_failed: "Scan",
  connector_sync_success: "Connector",
  connector_sync_failure: "Connector",
  vendor_risk_change: "Vendor",
};

const CATEGORY_BY_MODULE = {
  ai_cspm: "CSPM",
  ai_dspm: "DSPM",
  vapt: "Vulnerability",
  virtual_soc: "SOC Alert",
  compliance: "Compliance",
  tprm: "Vendor",
  risk_management: "Risk",
  asset_management: "Asset",
};

function categoryLabel(notification) {
  if (notification.category) {
    return notification.category;
  }
  if (notification.event_type) {
    if (CATEGORY_BY_EVENT_TYPE[notification.event_type]) {
      return CATEGORY_BY_EVENT_TYPE[notification.event_type];
    }
    return notification.event_type
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return CATEGORY_BY_MODULE[notification.module] || notification.module || "Notification";
}

const SEVERITY_ICON = {
  error: ShieldAlert,
  warning: AlertTriangle,
  success: CheckCircle2,
  info: Info,
};

function relativeTime(iso) {
  // iOS-style short timestamp ("now", "5m", "3h", "2d") rather than a
  // verbose "5 minutes ago" string, to match the compact notification
  // row style.
  if (!iso) return "";
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "now";
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  return date.toLocaleDateString();
}

/**
 * Self-contained, module-scoped notification panel. Deliberately does NOT
 * use the global NotificationContext (that context has no `module` filter
 * and mixes every module's notifications together) - each dashboard
 * mounts its own <NotificationCenter module="cspm" /> and only ever sees
 * that module's own notifications (notifications.views.list_notifications
 * already supports ?module=, see notifications/views.py), matching the
 * "module-specific, never leaks into another module's dashboard"
 * requirement.
 *
 * Always-visible panel (not a bell/dropdown) - shows up to 5 notifications
 * at once with an internal scroll for the rest, so the dashboard's own
 * height never grows just because more notifications arrive.
 */
const NotificationCenter = ({ module, title, extraItems = [], panelClassName = "bg-white" }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const userId = user?.id || user?._id || null;

  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const fetchList = useCallback(async () => {
    if (!userId || !module) return;
    try {
      const data = await getNotificationsApi(userId, {
        module,
        page: 1,
        page_size: 20,
      });
      setItems(data?.results || []);
      setUnreadCount(data?.pagination?.total_count != null
        ? (data.results || []).filter((n) => !n.is_read).length
        : 0);
    } catch (_) {
      /* silent - never break the dashboard over a notification fetch */
    } finally {
      setLoading(false);
    }
  }, [userId, module]);

  useEffect(() => {
    setLoading(true);
    fetchList();
    pollRef.current = setInterval(fetchList, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchList]);

  const handleMarkAllRead = async () => {
    try {
      await markAllReadApi(userId, module);
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (_) {
      /* silent */
    }
  };

  const handleClearAll = async () => {
    try {
      await clearAllNotificationsApi(userId, module);
      setItems([]);
      setUnreadCount(0);
    } catch (_) {
      /* silent */
    }
  };

  const handleNotificationClick = async (notification) => {
    if (notification._local) {
      // extraItems are client-side-only display items (e.g. Recent
      // Findings/Recent Activity rows) - never persisted, never hit the
      // notifications API, just navigate if a route was given.
      if (notification.reference_url) navigate(notification.reference_url);
      return;
    }
    if (!notification.is_read) {
      try {
        await markReadApi(notification.id);
        setItems((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, is_read: true } : n,
          ),
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch (_) {
        /* silent */
      }
    }
    if (notification.reference_url) {
      // Every target produced by the backend so far is a same-origin,
      // relative route (e.g. "/ai-cspm?tab=findings") - navigate() keeps
      // this an SPA transition instead of a full page reload.
      navigate(notification.reference_url);
    }
  };

  return (
    <div className={`${panelClassName} rounded-2xl p-3 h-full flex flex-col`}>
      <div className="flex items-center justify-between px-1 pb-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-500">
            {title || "Notifications"}
          </h3>
          {unreadCount > 0 && (
            <span
              className="text-[11px] font-bold px-1.5 py-0.5 rounded-full text-white min-w-[18px] text-center"
              style={{ backgroundColor: BRAND_COLOR }}
            >
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAllRead}
              className="flex items-center gap-1 text-xs font-medium hover:underline"
              style={{ color: BRAND_COLOR }}
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Mark all read
            </button>
          )}
          {(items.length > 0 || extraItems.length > 0) && (
            <button
              type="button"
              onClick={handleClearAll}
              className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-red-500"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear all
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-1">
        {loading && items.length === 0 && extraItems.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-10">
            Loading...
          </p>
        ) : items.length === 0 && extraItems.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-10">
            No notifications yet.
          </p>
        ) : (
          [...extraItems, ...items].map((n) => {
            const SeverityIcon = SEVERITY_ICON[n.notification_type] || Bell;
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => handleNotificationClick(n)}
                className={`relative w-full text-left rounded-2xl px-3 py-2.5 flex gap-3 items-start active:scale-[0.99] transition-all ${
                  !n.is_read
                    ? "bg-white shadow-sm ring-1 ring-black/5 hover:shadow-md"
                    : "bg-gray-100 hover:bg-gray-200/70"
                }`}
              >
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-white">
                  <SeverityIcon className="w-[18px] h-[18px] text-gray-500" strokeWidth={2.2} />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={`text-[13px] truncate ${
                        !n.is_read ? "font-semibold text-gray-900" : "font-normal text-gray-500"
                      }`}
                    >
                      {categoryLabel(n)}
                    </span>
                    <span className="text-[11px] text-gray-400 flex-shrink-0">
                      {relativeTime(n.created_at)}
                    </span>
                  </div>
                  <p
                    className={`text-[13px] leading-snug line-clamp-2 ${
                      !n.is_read ? "text-gray-700" : "text-gray-400"
                    }`}
                  >
                    {n.message}
                  </p>
                </div>

                {!n.is_read && (
                  <span
                    className="absolute top-2.5 right-2.5 w-2 h-2 rounded-full"
                    style={{ backgroundColor: BRAND_COLOR }}
                  />
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;
